import {
    useLayoutEffect,
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import type {
    View
} from "react-native";
import type IPopoverProps from "./type";
import PopoverAnchor from "./components/anchor";
import PopoverClose from "./components/close";
import PopoverContent from "./components/content";
import PopoverFooter from "./components/footer";
import PopoverTrigger from "./components/trigger";
import {
    PopoverContext
} from "./context";
import {
    warnOnce
} from "../../utils/development";

const Popover: FC<IPopoverProps> = ({
    touchBehaviour = "bottomSheet",
    isAvoidCollisions = true,
    isDefaultOpen = false,
    isOpen: isOpenProp,
    align = "start",
    isModal = false,
    side = "bottom",
    size = "medium",
    customLocalize,
    onOpenChange,
    customTheme,
    isVisible,
    children,
    onClose,
    onOpen
}) => {
    const baseId = useId();

    const [
        isInternalOpen,
        setIsInternalOpen
    ] = useState(isDefaultOpen);

    const triggerRef = useRef<View>(null);

    const anchorRef = useRef<View>(null);

    const [
        isAnchored,
        setIsAnchored
    ] = useState(false);

    const isSkipOpenCallbackRef = useRef(false);

    const controlledOpen = isOpenProp ?? isVisible;

    const isControlled = controlledOpen !== undefined;

    const isOpen = isControlled ? controlledOpen : isInternalOpen;

    const isOpenRef = useRef(isOpen);

    const handleClose = useCallback(() => {
        if(!isOpenRef.current) {
            return;
        }

        if(!isControlled) {
            isOpenRef.current = false;

            setIsInternalOpen(false);
        }

        onOpenChange?.(false);

        onClose?.();
    }, [
        isControlled,
        onOpenChange,
        onClose
    ]);

    const handleOpen = useCallback(() => {
        if(isOpenRef.current) {
            return;
        }

        onOpenChange?.(true);

        if(isControlled) {
            isSkipOpenCallbackRef.current = true;

            onOpen?.();

            return;
        }

        isOpenRef.current = true;

        setIsInternalOpen(true);
    }, [
        isControlled,
        onOpenChange,
        onOpen
    ]);

    const handleToggle = useCallback(() => {
        if(isOpenRef.current) {
            handleClose();

            return;
        }

        handleOpen();
    }, [
        handleClose,
        handleOpen
    ]);

    useLayoutEffect(() => {
        isOpenRef.current = isOpen;
    }, [
        isOpen
    ]);

    useEffect(() => {
        if(!isOpen) {
            return;
        }

        if(isSkipOpenCallbackRef.current) {
            isSkipOpenCallbackRef.current = false;

            return;
        }

        onOpen?.();
    }, [
        isOpen
    ]);

    useEffect(() => {
        if(isVisible === undefined) {
            return;
        }

        warnOnce({
            message: "Popover: \"isVisible\" is deprecated and will be removed in the next major version. Use \"isOpen\" instead."
        });
    }, [
        isVisible
    ]);

    useEffect(() => {
        if(onOpen === undefined) {
            return;
        }

        warnOnce({
            message: "Popover: \"onOpen\" is deprecated and will be removed in the next major version. Use \"onOpenChange\" instead."
        });
    }, [
        onOpen
    ]);

    useEffect(() => {
        if(onClose === undefined) {
            return;
        }

        warnOnce({
            message: "Popover: \"onClose\" is deprecated and will be removed in the next major version. Use \"onOpenChange\" instead."
        });
    }, [
        onClose
    ]);

    const context = useMemo(() => ({
        contentId: `${baseId}-content`,
        isAvoidCollisions,
        customLocalize,
        touchBehaviour,
        setIsAnchored,
        handleToggle,
        customTheme,
        handleClose,
        handleOpen,
        isAnchored,
        triggerRef,
        anchorRef,
        isModal,
        isOpen,
        align,
        side,
        size
    }), [
        isAvoidCollisions,
        touchBehaviour,
        customLocalize,
        handleToggle,
        handleClose,
        customTheme,
        handleOpen,
        isAnchored,
        isModal,
        baseId,
        isOpen,
        align,
        side,
        size
    ]);

    return <PopoverContext.Provider
        value={context}
    >
        {children}
    </PopoverContext.Provider>;
};
export default Object.assign(Popover, {
    Content: PopoverContent,
    Trigger: PopoverTrigger,
    Anchor: PopoverAnchor,
    Footer: PopoverFooter,
    Close: PopoverClose
});
